import { VeloIcon } from '@/components/icons';
import { VEHICULE_TYPE_OPTIONS } from '@/config/constants';
import vehiculeContent from '@/content/1-vehicule';
import type { FormSchemaType } from '@/schema';
import { BulleAide, FieldCheckbox, FieldInput, FieldPatternInput, FieldRadioGroup, Question } from '@workspace/core-ts/components';
import { SmartFormGlobal, useSmartFormGlobal } from '@workspace/core-ts/contexts';
import { useAvenant, useContent } from '@workspace/core-ts/hooks';
import { isNotInTheFuture, isOlderThan6Months, isPriceInRange } from '@workspace/utils';
import { useEffect } from 'react';

export default function VotreVelo() {
    const methods = useSmartFormGlobal<FormSchemaType>();
    const r = useContent();
    const isAvenant = useAvenant();
    const motifAvenant = methods.watch('DemandeTarif.Police.MotifAvenant');
    const typeVehicule = methods.watch('DemandeTarif.Vehicule.GenreVehicule');
    const isChangementVehicule = isAvenant && motifAvenant === '24';
    const isMiseAJourDeclaration = isAvenant && motifAvenant === '8';

    useEffect(() => {
        if (isChangementVehicule) {
            methods.setValue('DemandeTarif.Vehicule.GenreVehicule', undefined);
            methods.setValue('DemandeTarif.Vehicule.Designation', undefined);
            methods.setValue('DemandeTarif.Vehicule.ValeurVehicule', undefined);
            methods.setValue('DemandeTarif.Vehicule.DateMEC', undefined);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isChangementVehicule]);

    return (
        <SmartFormGlobal
            {...methods}
            className="mx-auto"
        >
            {/* Question du type de vélo */}
            <Question className="max-w-3xl">
                <Question.Header>
                    <VeloIcon />
                    <Question.Title>{r(vehiculeContent.typeVeloTitle)}</Question.Title>
                </Question.Header>
                <BulleAide className="mb-4">{r(vehiculeContent.bulleAideTypeVelo)}</BulleAide>
                <FieldRadioGroup
                    name="DemandeTarif.Vehicule.GenreVehicule"
                    options={VEHICULE_TYPE_OPTIONS}
                    col={3}
                />
                <FieldCheckbox
                    name="DemandeTarif.Vehicule.UsageNonLivraison"
                    label={r(vehiculeContent.declarationUsageTitle)}
                    validation={{
                        required: 'Cette déclaration est requise',
                    }}
                />
            </Question>

            {/* Question de déclaration de puissance pour VAE et VELC */}
            {(typeVehicule === 'VAE' || typeVehicule === 'VELC') && (
                <Question>
                    <Question.Header>
                        <VeloIcon />
                        <Question.Title className="text-xl">{r(vehiculeContent.declarationPuissanceTitle)}</Question.Title>
                    </Question.Header>
                    <FieldRadioGroup
                        name="declaration_vehicule"
                        options={[
                            { label: 'Oui', value: true },
                            { label: 'Non', value: false },
                        ]}
                        validation={{
                            validate: {
                                mustBeTrue: (value: boolean) => (value ? true : 'Vous devez confirmer que votre vélo respecte ces critères'),
                            },
                        }}
                    />
                </Question>
            )}

            {/* Question de la marque */}
            <Question>
                <Question.Header>
                    <Question.Title>{r(vehiculeContent.marqueTitle)}</Question.Title>
                </Question.Header>
                <BulleAide className="mb-3">{r(vehiculeContent.bulleAideMarque)}</BulleAide>
                <FieldInput
                    name="DemandeTarif.Vehicule.Designation"
                    placeholder="Exemple : Rockrider"
                    validation={{ required: 'La marque est requise' }}
                />
            </Question>

            {/* Question de la valeur d'achat */}
            <Question>
                <Question.Header>
                    <Question.Title>{r(vehiculeContent.valeurTitle)}</Question.Title>
                </Question.Header>
                <BulleAide className="mb-3">{r(vehiculeContent.bulleAideValeur)}</BulleAide>
                <FieldInput
                    name="DemandeTarif.Vehicule.ValeurVehicule"
                    type="number"
                    placeholder="2000"
                    validation={{
                        required: "La valeur d'achat est requise",
                        validate: {
                            isPriceInRange: isPriceInRange(800, 6000),
                        },
                    }}
                />
            </Question>

            {/* Question de la date d'achat */}
            <Question>
                <Question.Header>
                    <Question.Title>{r(vehiculeContent.dateAchatTitle)}</Question.Title>
                </Question.Header>
                <BulleAide className="mb-3">{r(vehiculeContent.bulleAideAchat)}</BulleAide>
                <FieldPatternInput
                    name="DemandeTarif.Vehicule.DateMEC"
                    type="date"
                    validation={{
                        required: "La date d'achat est requise",
                        validate: {
                            isNotInTheFuture,
                            ...(!isMiseAJourDeclaration && { isOlderThan6Months }),
                        },
                    }}
                />
            </Question>
        </SmartFormGlobal>
    );
}
